import { skipToken, useSuspenseQuery } from "@apollo/client/react";
import { isBefore, parseISO, subHours } from "date-fns";
import { Eye, FlaskConical, MoreHorizontal, Pencil, Play, RefreshCw } from "lucide-react";
import { type ReactNode, Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { z } from "zod";
import type { DryRunOptions, JobLogLevel } from "~/common/types";
import type { JobRun } from "~/components/job-log";
import { DetailLoading } from "~/components/loading";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui";
import { useDipLifecycle } from "~/features/dataImportProjects/context";
import {
  GetBatchEvidencesJobDocument,
  type GetBatchEvidencesJobQuery,
  type GetBatchEvidencesJobQueryVariables,
  GetBatchEvidencesJobsDocument,
} from "~/features/dataImportProjects/gqls";
import { useBatchEvidencesJobActions } from "~/features/dataImportProjects/hooks/useBatchEvidencesJobActions";
import { useDipResourceMutationGuard } from "~/features/dataImportProjects/hooks/useDipResourceMutationGuard";
import { useDryRunJobStorage } from "~/features/dataImportProjects/hooks/useDryRunJobStorage";
import { useSubmitBatchEvidencesJob } from "~/features/dataImportProjects/hooks/useSubmitBatchEvidencesJob";
import { BatchEvidencesJobType } from "~/features/dataImportProjects/types";
import { useTenant } from "~/features/tenant/context";
import { cn } from "~/lib/utils";
import { rootRoute as R } from "~/routes";
import { v2Route } from "~/v2/routes";
import { DryRunResultDialog } from "./BatchEvidencesJobDryRunResultDialog";
import { BatchEvidencesJobForm } from "./BatchEvidencesJobForm";
import { JobLogDialog } from "./BatchEvidencesJobLogDialog";
import { DipReadOnlyTooltip } from "./DipReadOnlyTooltip";

type BatchEvidencesJobQueryRow = GetBatchEvidencesJobQuery["batchEvidencesJobs"][number];
type BatchEvidencesJobRun = BatchEvidencesJobQueryRow["batchEvidencesJobRuns"][number];
type BatchEvidencesJob = Omit<BatchEvidencesJobQueryRow, "batchEvidencesJobRuns"> & {
  batchEvidencesJobRuns: (Omit<BatchEvidencesJobRun, "user"> & { user: BatchEvidencesJobRun["user"] | null })[];
};

type BatchEvidencesJobDetailPanelProps = {
  jobId: string;
  mode?: "peek" | "page";
};

type MetadataRowProps = {
  label: string;
  children: ReactNode;
};

const zBatchEvidencesJobRunResult = z.object({
  draftCount: z.number(),
  evidenceCount: z.number(),
});

function MetadataRow({ label, children }: MetadataRowProps) {
  return (
    <div className="grid grid-cols-[140px_minmax(0,1fr)] gap-3">
      <dt className="text-12-regular text-fg-3">{label}</dt>
      <dd className="min-w-0 text-14-regular text-fg-1">{children}</dd>
    </div>
  );
}

function formatDate(t: ReturnType<typeof useTranslation>["t"], iso: string | null | undefined) {
  return iso ? t("date.short", { date: parseISO(iso) }) : "-";
}

function formatJobResultCount(raw: unknown): string {
  const parsed = zBatchEvidencesJobRunResult.safeParse(raw);
  if (!parsed.success) {
    return "-";
  }
  return String(parsed.data.draftCount + parsed.data.evidenceCount);
}

function DryRunOptionsDialog({
  open,
  onOpenChange,
  onConfirm,
  readOnly,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (options: DryRunOptions, logLevel?: JobLogLevel) => void;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const [limit, setLimit] = useState("0");
  const [offset, setOffset] = useState("0");
  const [logLevel, setLogLevel] = useState<JobLogLevel>("info");

  const confirm = () => {
    if (readOnly) return;
    onConfirm(
      {
        limit: limit ? Number(limit) || undefined : undefined,
        offset: offset ? Number(offset) || undefined : undefined,
      },
      logLevel !== "info" ? logLevel : undefined,
    );
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("feature.dataImportProjects.dryRunOptions.title")}</DialogTitle>
          <DialogDescription>{t("feature.dataImportProjects.dryRunOptions.description")}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="be-job-dry-run-limit">{t("ui.limit")}</Label>
            <Input
              id="be-job-dry-run-limit"
              inputMode="numeric"
              value={limit}
              onChange={(event) => setLimit(event.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="be-job-dry-run-offset">{t("ui.offset")}</Label>
            <Input
              id="be-job-dry-run-offset"
              inputMode="numeric"
              value={offset}
              onChange={(event) => setOffset(event.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label>{t("ui.logLevel")}</Label>
            <Select value={logLevel} onValueChange={(value) => setLogLevel(value as JobLogLevel)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="info">{t("ui.logLevelInfo")}</SelectItem>
                <SelectItem value="debug">{t("ui.logLevelDebug")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            {t("ui.cancel")}
          </Button>
          <Button type="button" onClick={confirm}>
            {t("feature.dataImportProjects.dryRunOptions.runDryRun")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RunJobDialog({
  open,
  onOpenChange,
  onConfirm,
  readOnly,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (logLevel?: JobLogLevel) => void;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const [logLevel, setLogLevel] = useState<JobLogLevel>("info");

  const confirm = () => {
    if (readOnly) return;
    onConfirm(logLevel !== "info" ? logLevel : undefined);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("feature.dataImportProjects.detail.operation.runJob")}</DialogTitle>
          <DialogDescription>
            {t("feature.dataImportProjects.detail.operation.runJobConfirmDescription")}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          <Label>{t("ui.logLevel")}</Label>
          <Select value={logLevel} onValueChange={(value) => setLogLevel(value as JobLogLevel)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="info">{t("ui.logLevelInfo")}</SelectItem>
              <SelectItem value="debug">{t("ui.logLevelDebug")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            {t("ui.cancel")}
          </Button>
          <Button type="button" onClick={confirm}>
            {t("feature.dataImportProjects.detail.operation.runJob")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BatchEvidencesJobEditBridge({
  open,
  onOpenChange,
  job,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  job: BatchEvidencesJob;
}) {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {t("ui.edit")} {t("model.batchEvidencesJob.modelName")}
          </DialogTitle>
          <DialogDescription>
            {t("feature.dataImportProjects.v2.temporaryLegacyBridge", {
              defaultValue: "Temporary legacy component bridge",
            })}
          </DialogDescription>
        </DialogHeader>
        <Suspense fallback={<DetailLoading />}>
          <BatchEvidencesJobForm
            batchEvidencesJobId={job.id}
            dataSourceId={job.dataSourceId}
            importProjectId={job.dataImportProjectId}
            onSubmit={() => onOpenChange(false)}
            refetchQueriesOnEdit={[
              {
                query: GetBatchEvidencesJobsDocument,
                variables: { tenantId, where: { dataSourceId: { _eq: job.dataSourceId } } },
              },
            ]}
          />
        </Suspense>
      </DialogContent>
    </Dialog>
  );
}

function JobRunsTable({
  job,
  dryRunJobRunIds,
  onOpenLog,
  onMarkFailed,
  readOnly,
}: {
  job: BatchEvidencesJob;
  dryRunJobRunIds: Set<string>;
  onOpenLog: (jobRun: JobRun) => void;
  onMarkFailed: (jobRunId: string) => void;
  readOnly: boolean;
}) {
  const { t } = useTranslation();

  if (job.batchEvidencesJobRuns.length === 0) {
    return (
      <p className="rounded-[var(--radius-md)] border border-border-default p-4 text-12-regular text-fg-3">
        {t("feature.common.noData")}
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-[var(--radius-md)] border border-border-default">
      <table className="w-full min-w-[820px] border-collapse text-12-regular">
        <thead className="bg-bg-subtle text-fg-2">
          <tr>
            <th className="border-b border-border-default px-3 py-2 text-left font-semibold">
              {t("model.common.createdAt")}
            </th>
            <th className="border-b border-border-default px-3 py-2 text-left font-semibold">
              {t("model.batchEvidencesJobRun.runStatusId")}
            </th>
            <th className="border-b border-border-default px-3 py-2 text-left font-semibold">{t("ui.type")}</th>
            <th className="border-b border-border-default px-3 py-2 text-right font-semibold">
              {t("model.batchEvidencesJobRun.evidencesCount")}
            </th>
            <th className="border-b border-border-default px-3 py-2 text-left font-semibold">
              {t("model.batchEvidencesJobRun.user")}
            </th>
            <th className="border-b border-border-default px-3 py-2 text-left font-semibold">{t("ui.actions")}</th>
          </tr>
        </thead>
        <tbody>
          {job.batchEvidencesJobRuns.map((jobRun) => {
            const isDryRun = dryRunJobRunIds.has(jobRun.id);
            const canMarkFailed =
              jobRun.statusId === "init" && isBefore(parseISO(jobRun.createdAt), subHours(new Date(), 2));
            return (
              <tr key={jobRun.id} className="border-t border-border-default first:border-t-0">
                <td className="px-3 py-2 text-fg-2">{t("date.long", { date: parseISO(jobRun.createdAt) })}</td>
                <td className="px-3 py-2 text-fg-1">{jobRun.statusId}</td>
                <td className="px-3 py-2 text-fg-2">{isDryRun ? t("ui.dryRun") : t("ui.normal")}</td>
                <td className="px-3 py-2 text-right text-fg-2">{formatJobResultCount(jobRun.jobResult)}</td>
                <td className="px-3 py-2 text-fg-2">{jobRun.user?.email ?? "—"}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        onOpenLog({
                          jobRunId: jobRun.id,
                          s3LogPath: jobRun.s3LogPath,
                          jobName: "BatchEvidencesJob",
                          dryRun: isDryRun,
                        })
                      }
                    >
                      <Eye className="size-3.5" />
                      {t("ui.view")}
                    </Button>
                    {canMarkFailed ? (
                      <DipReadOnlyTooltip disabled={readOnly} ariaLabel={t("ui.markAsFailed")}>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={readOnly}
                          onClick={() => {
                            if (readOnly) return;
                            onMarkFailed(jobRun.id);
                          }}
                        >
                          {t("ui.markAsFailed")}
                        </Button>
                      </DipReadOnlyTooltip>
                    ) : null}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function BatchEvidencesJobInfo({ job }: { job: BatchEvidencesJob }) {
  const { t } = useTranslation();

  return (
    <section className="grid gap-6 lg:grid-cols-[minmax(260px,0.85fr)_minmax(0,1.15fr)]">
      <dl className="grid gap-3">
        <MetadataRow label={t("model.common.name")}>{job.name || `(${t("ui.noName")})`}</MetadataRow>
        <MetadataRow label={t("ui.type")}>{job.jobTypeId}</MetadataRow>
        {job.jobTypeId === BatchEvidencesJobType.DbBasedScript ? (
          <MetadataRow label={t("model.evidenceImporter.modelName")}>
            {job.evidenceImporter ? (
              <span className="inline-flex flex-wrap items-center gap-2">
                {job.evidenceImporter.deactivatedAt ? <Badge tone="status-draft">{t("ui.deactivated")}</Badge> : null}
                <Link
                  className="text-primary-700 hover:underline"
                  to={v2Route.dataImportProjects.detail.$buildPath({
                    params: { id: job.dataImportProjectId },
                    searchParams: { peekType: "evidenceImporter", peekId: job.evidenceImporter.id },
                  })}
                >
                  {job.evidenceImporter.name}
                </Link>
              </span>
            ) : (
              "-"
            )}
          </MetadataRow>
        ) : (
          <MetadataRow label={t("model.batchChangeRequestsJob.jobType.s3Script")}>{job.s3Path || "-"}</MetadataRow>
        )}
        {job.deactivatedAt ? (
          <MetadataRow label={t("model.common.deactivatedAt")}>
            <span className="text-score-c">{formatDate(t, job.deactivatedAt)}</span>
          </MetadataRow>
        ) : null}
      </dl>

      <dl className="grid gap-3">
        <MetadataRow label={t("model.evidenceSet.modelName")}>
          <Link
            className="text-primary-700 hover:underline"
            to={v2Route.dataImportProjects.detail.evidenceSet.$buildPath({
              params: { id: job.dataImportProjectId, evidenceSetId: job.evidenceSet.id },
            })}
          >
            {job.evidenceSet.name || `(${t("ui.noName")})`}
          </Link>
        </MetadataRow>
      </dl>
    </section>
  );
}

function BatchEvidencesJobDetailPanelContent({
  job,
  refetch,
  mode,
  canExecuteMutation,
}: {
  job: BatchEvidencesJob;
  refetch: () => void;
  mode: "peek" | "page";
  canExecuteMutation: () => boolean;
}) {
  const { t } = useTranslation();
  const { isReadOnly } = useDipLifecycle();
  const [openEditForm, setOpenEditForm] = useState(false);
  const [logDialogJobRun, setLogDialogJobRun] = useState<JobRun | null>(null);
  const [openDryRunResult, setOpenDryRunResult] = useState(false);
  const [openDryRunOptionsDialog, setOpenDryRunOptionsDialog] = useState(false);
  const [openRunJobDialog, setOpenRunJobDialog] = useState(false);
  useEffect(() => {
    if (!isReadOnly) return;
    setOpenEditForm(false);
    setOpenDryRunOptionsDialog(false);
    setOpenRunJobDialog(false);
  }, [isReadOnly]);
  const { dryRunJobRunIds, saveDryRunJobId } = useDryRunJobStorage(job.id, "batch_evidences_dry_run_");
  const { onActivateClick, onDeactivateClick, markJobRunAsFailed } = useBatchEvidencesJobActions({
    refetch,
    dataSourceId: job.dataSourceId,
    canExecute: canExecuteMutation,
  });
  const { submit, processing } = useSubmitBatchEvidencesJob({
    jobId: job.id,
    jobType: job.jobTypeId as BatchEvidencesJobType,
    importerDeactivatedAt: job.evidenceImporter?.deactivatedAt,
    refetch,
    skipConfirm: true,
    setLogDialogJobRun: (jobRun) => {
      if (jobRun?.dryRun) {
        saveDryRunJobId(jobRun.jobRunId);
      }
      setLogDialogJobRun(jobRun);
    },
  });
  const isS3Script = job.jobTypeId === BatchEvidencesJobType.S3Script;

  const handleDryRunConfirm = (options: DryRunOptions, logLevel?: JobLogLevel) => {
    if (!canExecuteMutation()) return;
    submit(true, options, logLevel);
  };

  return (
    <div className={cn("space-y-5", mode === "page" && "mx-auto max-w-[var(--content-max)] p-8")}>
      <section className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className={cn("text-fg-1", mode === "page" ? "text-dewey-title" : "text-[18px] font-semibold")}>
                {job.name || `(${t("ui.noName")})`}
              </h2>
              {job.deactivatedAt ? <Badge tone="status-draft">{t("ui.deactivated")}</Badge> : null}
            </div>
            <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-12-regular text-fg-3">
              <div className="flex gap-1.5">
                <dt>{t("model.common.id")}:</dt>
                <dd className="font-mono">{job.id}</dd>
              </div>
              <div className="flex gap-1.5">
                <dt>{t("model.common.createdAt")}:</dt>
                <dd>{formatDate(t, job.createdAt)}</dd>
              </div>
              <div className="flex gap-1.5">
                <dt>{t("model.common.updatedAt")}:</dt>
                <dd>{formatDate(t, job.updatedAt)}</dd>
              </div>
            </dl>
          </div>

          <div className="flex flex-wrap justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label={t("ui.refresh")}
              onClick={() => void refetch()}
            >
              <RefreshCw className="size-4" />
            </Button>
            <DipReadOnlyTooltip disabled={isReadOnly} ariaLabel={t("ui.dryRun")}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={processing.value || isS3Script || isReadOnly}
                onClick={() => {
                  if (!canExecuteMutation()) return;
                  setOpenDryRunOptionsDialog(true);
                }}
              >
                <FlaskConical className="size-3.5" />
                {processing.value && processing.dryRun ? t("ui.processing") : t("ui.dryRun")}
              </Button>
            </DipReadOnlyTooltip>
            <DipReadOnlyTooltip
              disabled={isReadOnly}
              ariaLabel={t("feature.dataImportProjects.detail.operation.runJob")}
            >
              <Button
                type="button"
                size="sm"
                disabled={processing.value || isS3Script || isReadOnly}
                onClick={() => {
                  if (!canExecuteMutation()) return;
                  setOpenRunJobDialog(true);
                }}
              >
                <Play className="size-3.5" />
                {processing.value && !processing.dryRun
                  ? t("ui.processing")
                  : t("feature.dataImportProjects.detail.operation.runJob")}
              </Button>
            </DipReadOnlyTooltip>
            <DipReadOnlyTooltip disabled={isReadOnly} ariaLabel={t("ui.edit")}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={processing.value || isS3Script || isReadOnly}
                onClick={() => {
                  if (!canExecuteMutation()) return;
                  setOpenEditForm(true);
                }}
              >
                <Pencil className="size-3.5" />
                {t("ui.edit")}
              </Button>
            </DipReadOnlyTooltip>
            <DropdownMenu>
              <DipReadOnlyTooltip disabled={isReadOnly} ariaLabel={t("ui.operation", { defaultValue: "Operation" })}>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    disabled={isReadOnly}
                    aria-label={t("ui.operation", { defaultValue: "Operation" })}
                  >
                    <MoreHorizontal className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
              </DipReadOnlyTooltip>
              <DropdownMenuContent align="end">
                {job.deactivatedAt ? (
                  <DropdownMenuItem
                    onClick={() => {
                      if (!canExecuteMutation()) return;
                      onActivateClick({ id: job.id, name: job.name ?? "this evidences job" });
                    }}
                  >
                    {t("ui.activate")}
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    onClick={() => {
                      if (!canExecuteMutation()) return;
                      onDeactivateClick({ id: job.id, name: job.name ?? "this evidences job" });
                    }}
                  >
                    {t("ui.deactivate")}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </section>

      <BatchEvidencesJobInfo job={job} />

      <section className="space-y-3">
        <h3 className="text-14-bold text-fg-1">{t("ui.history")}</h3>
        <JobRunsTable
          job={job}
          dryRunJobRunIds={dryRunJobRunIds}
          onOpenLog={setLogDialogJobRun}
          onMarkFailed={(jobRunId) => {
            if (!canExecuteMutation()) return;
            markJobRunAsFailed({ jobRunId });
          }}
          readOnly={isReadOnly}
        />
      </section>

      {!isReadOnly ? (
        <BatchEvidencesJobEditBridge
          open={openEditForm && !isReadOnly}
          onOpenChange={(open) => {
            if (!open || canExecuteMutation()) setOpenEditForm(open);
          }}
          job={job}
        />
      ) : null}
      <DryRunOptionsDialog
        open={openDryRunOptionsDialog && !isReadOnly}
        onOpenChange={(open) => {
          if (!open || canExecuteMutation()) setOpenDryRunOptionsDialog(open);
        }}
        onConfirm={handleDryRunConfirm}
        readOnly={isReadOnly}
      />
      <RunJobDialog
        open={openRunJobDialog && !isReadOnly}
        onOpenChange={(open) => {
          if (!open || canExecuteMutation()) setOpenRunJobDialog(open);
        }}
        onConfirm={(logLevel) => {
          if (!canExecuteMutation()) return;
          submit(false, undefined, logLevel);
        }}
        readOnly={isReadOnly}
      />
      <JobLogDialog
        jobRun={logDialogJobRun}
        onOpenChange={(open) => !open && setLogDialogJobRun(null)}
        onFetchResult={() => setOpenDryRunResult(true)}
      />
      <DryRunResultDialog open={openDryRunResult} onOpenChange={setOpenDryRunResult} jobRun={logDialogJobRun} />
    </div>
  );
}

export function BatchEvidencesJobDetailPanel({ jobId, mode = "peek" }: BatchEvidencesJobDetailPanelProps) {
  const { t } = useTranslation();
  const { projectId } = useDipLifecycle();
  const { canExecuteMutation, setAuthorized } = useDipResourceMutationGuard(`batch-evidences-job:${jobId}`);
  const { tenantId } = useTenant();
  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }
  const { data, refetch } = useSuspenseQuery<GetBatchEvidencesJobQuery, GetBatchEvidencesJobQueryVariables>(
    GetBatchEvidencesJobDocument,
    jobId ? { variables: { tenantId, id: jobId } } : skipToken,
  );
  const job = data?.batchEvidencesJobs[0];
  const isAuthorized = job?.dataImportProjectId === projectId;
  setAuthorized(isAuthorized);

  if (!job || !isAuthorized) {
    return <p className="text-12-regular text-fg-3">{t("feature.common.noData")}</p>;
  }

  return (
    <BatchEvidencesJobDetailPanelContent
      job={job}
      refetch={() => void refetch()}
      mode={mode}
      canExecuteMutation={canExecuteMutation}
    />
  );
}
